import type { FilterRuleModel, Metric } from '@/shared/generated'
import type { MetricsWidgetType } from '../lib/helpers'
import type { MetricsDataUnit } from '../model/types'
import type { FormattedMetric } from './MetricRow'

import { entries, map, pipe, prop } from 'lodash/fp'
import { AlertTriangleIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { useSelectedCurrency } from '@/entities/currency'
import { useOptimizationQuery } from '@/entities/optimization'
import {
	useInterpretabilityStatisticQuery,
	useRepricingStatisticQuery,
} from '@/entities/optimization-statistic'

import { Alert, AlertDescription } from '@/shared/components/ui/alert'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { useOptimizationGroupId } from '@/shared/hooks/useOptimzationGroupId'

import {
	getMetricCurrency,
	getMetricDiffByType,
	getTypesByMetricSchema,
	METRICS_WIDGET_TYPES,
	selectAdditionalMetrics,
	selectCustomMetrics,
	selectTargetMetrics,
} from '../lib/helpers'
import { MetricRow } from './MetricRow'

export default function OptimizationMetricsWidget({
	filters,
	widgetType = METRICS_WIDGET_TYPES.TARGET,
	metricsSchema,
}: {
	filters: FilterRuleModel[]
	widgetType: MetricsWidgetType
	metricsSchema: Metric[]
}) {
	const { t } = useTranslation()

	const optimizationGroupId = useOptimizationGroupId()!
	const currency = useSelectedCurrency()
	const optimizationQuery = useOptimizationQuery(optimizationGroupId)
	const repricingStatisticQuery = useRepricingStatisticQuery(
		{
			optimizations: [
				{
					optimization_id: optimizationQuery.data!.id,
					optimization_group_id: optimizationGroupId,
				},
			],
			filters: [],
		},
		{
			enabled: !!optimizationGroupId && !!optimizationQuery.data?.id,
			select: prop('settings.strategy'),
		}
	)
	const interpretabilityStatistic = useInterpretabilityStatisticQuery(
		{
			optimizations: [
				{
					optimization_group_id: optimizationGroupId,
					optimization_id: optimizationQuery.data!.id,
				},
			],
			filters,
			currency,
		},
		{
			enabled:
				!!optimizationQuery.data?.id &&
				!!repricingStatisticQuery.data &&
				!!metricsSchema,
			select: pipe([
				getMetricsByWidgetType(widgetType, repricingStatisticQuery.data),
				entries,
				map((data: [string, MetricsDataUnit]) =>
					formatMetric(data, metricsSchema, widgetType, currency)
				),
			]),
		}
	)
	if (interpretabilityStatistic.isLoading) {
		return <LoadingPlaceholder />
	}
	if (repricingStatisticQuery.isError || interpretabilityStatistic.isError) {
		return (
			<Alert variant='destructive' className='w-full'>
				<AlertTriangleIcon />
				<AlertDescription>
					{interpretabilityStatistic.error?.message ||
						repricingStatisticQuery.error?.message ||
						t('fatal_error_title')}
				</AlertDescription>
			</Alert>
		)
	}

	return (
		<div className='grid gap-1'>
			{interpretabilityStatistic.data?.map((item: FormattedMetric) => (
				<MetricRow key={item.key} item={item} />
			))}
		</div>
	)
}

function LoadingPlaceholder() {
	return (
		<div className='flex flex-col items-center space-y-2'>
			<div className='flex w-full items-center justify-between'>
				<Skeleton className='h-5 w-20' />
				<Skeleton className='h-5 w-10' />
			</div>
			<div className='flex w-full items-center justify-between'>
				<Skeleton className='h-5 w-24' />
				<Skeleton className='h-5 w-12' />
			</div>
		</div>
	)
}

const formatMetric = (
	[key, values]: [string, MetricsDataUnit],
	metricsSchema: Metric[],
	widgetType: string,
	currency?: string
): FormattedMetric => {
	const metricSchema = metricsSchema?.find(
		({ name }) => name.split('.')[0] === `${key}`
	)
	const { dataType, diffType } = getTypesByMetricSchema(metricSchema!)

	const data = {
		type: dataType,
		values,
	}
	const diff = {
		type: diffType,
		value: getMetricDiffByType(values, diffType),
	}

	return {
		widgetType,
		key,
		data,
		diff,
		values,
		description: metricSchema?.description || '',
		currency: getMetricCurrency(metricSchema, currency),
	}
}

const getMetricsByWidgetType = (
	widgetType: string,
	strategy: { target: string; protect: string }
) => {
	switch (widgetType) {
		case METRICS_WIDGET_TYPES.TARGET:
			return selectTargetMetrics(strategy)
		case METRICS_WIDGET_TYPES.ADDITIONAL:
			return selectAdditionalMetrics(strategy)
		default:
			return selectCustomMetrics()
	}
}
